/**
 * PilotSlideOver — Slide-over panel for Pilot chat in workspace mode.
 *
 * Appears from the right side, overlaying the workspace content.
 * Delegates to PilotPanel for actual chat logic.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import PilotPanel from './PilotPanel';
import type { StepOpenEvent } from '../hooks/usePilotChat';

interface PilotSlideOverProps {
  subProjectId: string;
  subProjectName: string;
  kbId?: string;
  kbName?: string;
  solutionId?: string | null;
  language: string;
  onClose: () => void;
  onStepOpen: (event: StepOpenEvent) => void;
}

const PilotSlideOver: React.FC<PilotSlideOverProps> = ({
  subProjectId,
  subProjectName,
  kbId,
  kbName,
  solutionId,
  language,
  onClose,
  onStepOpen,
}) => {
  const { t } = useTranslation();

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed right-0 top-0 h-full w-[360px] max-w-[90vw] bg-white shadow-xl z-50 flex flex-col">
        {/* Close button */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 flex-shrink-0">
          <span className="text-xs font-semibold text-slate-600">
            {t('pilot.title', 'Pilot')}
          </span>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1"
            title={t('common.close', 'Close')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Pilot chat */}
        <div className="flex-1 min-h-0 overflow-hidden">
          <PilotPanel
            subProjectId={subProjectId}
            subProjectName={subProjectName}
            kbId={kbId}
            kbName={kbName}
            solutionId={solutionId}
            language={language}
            onStepOpen={onStepOpen}
          />
        </div>
      </div>
    </>
  );
};

export default PilotSlideOver;
